/**
 * Cliente do Supabase.
 *
 * A chave anônima é pública por desenho: ela identifica o projeto, não autoriza
 * nada. Quem autoriza é o JWT do login, e o que ele alcança é decidido pelas
 * políticas de RLS — está tudo em `docs/BANCO.md`.
 *
 * Sem as variáveis configuradas o cliente não existe, e a aplicação cai no
 * adaptador de demonstração. É isso que mantém `pnpm test` e o modo offline
 * funcionando sem um servidor por perto.
 */

import { createClient } from '@supabase/supabase-js';
import type { SupabaseClient } from '@supabase/supabase-js';

import { comClinicaAtiva } from '@/session/clinicaAtiva';
import { armazenamentoDaSessao } from '@/session/lembrarDeMim';

const url = import.meta.env.VITE_SUPABASE_URL;
const chaveAnonima = import.meta.env.VITE_SUPABASE_ANON_KEY;

export const supabase: SupabaseClient | null = url && chaveAnonima
    ? createClient(url, chaveAnonima, {
        auth: {
            persistSession: true,
            autoRefreshToken: true,
            /* A sessão vive numa chave própria: dividir a chave com o protótipo
               faria um deslogar o outro. */
            storageKey: 'vec.auth',
            /* "Lembrar de mim": marcado, a sessão fica no localStorage; desmarcado, no
               sessionStorage e acaba com a aba (`lembrarDeMim.ts`). */
            storage: armazenamentoDaSessao,
        },
        global: {
            /* Todo pedido leva a clínica ativa: é o que faz o banco mostrar só a
               clínica escolhida a quem é membro de mais de uma (`0064`). */
            fetch: (entrada: RequestInfo | URL, init?: RequestInit) => fetch(entrada, {
                ...init,
                headers: comClinicaAtiva(init?.headers ?? (entrada instanceof Request ? entrada.headers : undefined)),
            }),
        },
    })
    : null;

export function temSupabase(): boolean {
    return supabase !== null;
}
